import { useQuery } from '@tanstack/react-query';

import { readOrganizationUsage } from '@/lib/api/generated/api';
import type { OrganizationUsageSummary } from '@/lib/api/generated/model';
import { UsageError, isForbidden } from '@/features/workspace-usage';

import { organizationKeys } from '../queryKeys';

/**
 * Every workspace's spend and applied limit, for a period (spec 073 US1, US2).
 *
 * The console's only read of substance: the overview's figures and the limits table are
 * two presentations of this one response, which is why they cannot disagree about what
 * the organization spent.
 *
 * `UsageError` is the workspace page's type, reused rather than copied. The two
 * failures a reader must be able to tell apart are the same on both surfaces — "you may
 * not see this" and "we could not find out" — and a second error type spelling them
 * differently is how one surface ends up explaining a refusal and the other throwing it
 * at an error boundary.
 */
export function useOrganizationUsage(period?: string) {
  return useQuery<OrganizationUsageSummary>({
    queryKey: organizationKeys.usage(period),
    queryFn: async ({ signal }) => {
      const response = await readOrganizationUsage(
        period === undefined ? {} : { period },
        { signal },
      );
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only an organization administrator can see what the organization is spending.',
        );
      }
      throw new UsageError(
        'unavailable',
        'Could not read the organization’s usage.',
      );
    },
    // No polling (FR-028). Spend changes when runs finish, and a console is not a
    // monitor; the mutations below invalidate this when the reader changes something.
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  });
}
